import { useState, useEffect } from "react";
import { X, Instagram, Flag, ChevronDown } from "lucide-react";
import { Button } from "./Button";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { useCountryTranslations } from "../contexts/CountryTranslationsContext";
import { useToast } from "../contexts/ToastContext";

interface FirstPredictionInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  onSkip?: () => void;
}

export function FirstPredictionInfoModal({
  isOpen,
  onClose,
  onComplete,
  onSkip,
}: FirstPredictionInfoModalProps) {
  const { t, language } = useLanguage();
  const { updateProfile } = useAuth();
  const { translations } = useCountryTranslations();
  const { showToast } = useToast();
  const [instagramHandle, setInstagramHandle] = useState("");
  const [supportedCountry, setSupportedCountry] = useState("");
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState<
    Array<{ code: string; name: string; flag: string }>
  >([]);

  useEffect(() => {
    // Convert translations map to array for dropdown, using current language
    const countriesArray = Array.from(translations.values())
      .map((country) => {
        let name = country.name_en;
        if (language === "fr") name = country.name_fr;
        else if (language === "ar") name = country.name_ar;

        return {
          code: country.country_code,
          name: name,
          flag: country.flag,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
    setCountries(countriesArray);
  }, [translations, language]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await updateProfile({
        instagram_handle: instagramHandle || null,
        supported_country: supportedCountry || null,
      });

      showToast(t("common.profileUpdated"), "success");
      onComplete();
      onClose();
    } catch (error) {
      showToast(
        error instanceof Error
          ? error.message
          : t("common.unexpectedError"),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200 my-auto max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-primary to-primary-dark p-6 text-white flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-bold">
              {t("firstPrediction.title")}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-smooth"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-white/90 text-sm">
            {t("firstPrediction.subtitle")}
          </p>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="p-6 space-y-4 overflow-y-auto flex-1"
        >
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-900">
              {t("login.instagram")}
            </label>
            <div className="relative">
              <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input
                type="text"
                value={instagramHandle}
                onChange={(e) => setInstagramHandle(e.target.value)}
                placeholder="@username"
                className="w-full border border-slate-200 rounded-xl py-3 pl-11 pr-4 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth bg-white"
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {t("login.instagramHint")}
            </p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-900">
              {t("login.supportedCountry")}
            </label>
            <div className="relative">
              <Flag className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 z-10" />
              <select
                value={supportedCountry}
                onChange={(e) => setSupportedCountry(e.target.value)}
                disabled={countries.length === 0}
                required
                className={`w-full border border-slate-200 rounded-xl py-3 pl-11 pr-10 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth bg-white appearance-none ${
                  countries.length === 0
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                <option value="">
                  {countries.length === 0
                    ? t("common.loading")
                    : t("login.selectCountry")}
                </option>
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.flag} {country.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 pointer-events-none" />
            </div>
          </div>

          <div className="space-y-3">
            <Button
              type="submit"
              variant="primary"
              className="w-full !py-3"
              disabled={loading || !supportedCountry}
            >
              {loading ? t("common.loading") : t("firstPrediction.continue")}
            </Button>
            
            {onSkip && (
              <Button
                type="button"
                variant="secondary"
                className="w-full !py-3"
                onClick={() => {
                  onSkip();
                  onClose();
                }}
                disabled={loading}
              >
                {t("common.notNow")}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

